/**
 * Admin Pricing Config UI (US-04-10 cost-based rewrite, replaces US-04-08 multiplier)
 *
 * Schema mới:
 *   - markup (1 knob global — cost × markup = sell price VND)
 *   - vnd_per_credit, usd_to_vnd
 *   - agents: { [type]: { model, output_ratio, chargeable } }
 *   - model_pricing: { [model]: { input, output } } (USD per 1M tokens)
 *
 * Công thức: credits = ceil( (in × m.in + out × m.out) × usd_to_vnd × markup / (1M × vnd_per_credit) )
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Save, RotateCcw, History, AlertCircle, Check, Plus, Trash2, Info } from 'lucide-react';

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';

interface AgentPricing {
  model: string;
  output_ratio: number;
  /** Per-agent markup override. null/undefined = inherit global. 0 = FREE. */
  markup?: number | null;
}
interface ModelPricing {
  input: number;
  output: number;
}
interface PricingConfig {
  agents: Record<string, AgentPricing>;
  model_pricing: Record<string, ModelPricing>;
  markup: number;
  vnd_per_credit: number;
  usd_to_vnd: number;
}

interface HistoryEntry {
  version: number;
  effective_from: string;
  config: PricingConfig;
  reason: string | null;
  updated_by: string | null;
}

const DEFAULT_CONFIG: PricingConfig = {
  agents: {
    translate:        { model: 'gemini-3.1-flash-lite', output_ratio: 1.30, markup: null },  // inherit global
    qc:               { model: 'gemini-3.1-flash-lite', output_ratio: 0.05, markup: null },
    audit:            { model: 'gemini-3-flash',        output_ratio: 0.25, markup: null },
    'semantic-match': { model: 'gemini-3.1-flash-lite', output_ratio: 0.02, markup: 0 },     // FREE by default (founder chọn)
  },
  model_pricing: {
    'gemini-3.1-flash-lite': { input: 0.25, output: 1.50 },
    'gemini-3-flash':        { input: 0.50, output: 3.00 },
    'gemini-2.5-flash-lite': { input: 0.10, output: 0.40 },
    'gemini-2.5-flash':      { input: 0.30, output: 2.50 },
  },
  markup: 7.5,
  vnd_per_credit: 0.133,
  usd_to_vnd: 25000,
};

const PricingConfigPanel: React.FC = () => {
  const { session } = useAuth();
  const [config, setConfig] = useState<PricingConfig | null>(null);
  const [editing, setEditing] = useState<PricingConfig | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [currentVersion, setCurrentVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [reason, setReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [previewInput, setPreviewInput] = useState(10000);  // preview 10K input tokens

  const token = session?.access_token;

  const fetchConfig = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/pricing-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = (await res.json()) as { config: PricingConfig | null; current_version: number };
      const loaded = data.config && data.config.agents ? data.config : DEFAULT_CONFIG;
      setConfig(loaded);
      setEditing(JSON.parse(JSON.stringify(loaded)));
      setCurrentVersion(data.current_version);
      if (!data.config || !data.config.agents) {
        setToast({ type: 'error', msg: 'DB chưa có schema mới — đang load DEFAULT. Nhấn Lưu để migrate.' });
      }
    } catch (err: any) {
      setToast({ type: 'error', msg: `Load fail: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/pricing-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as { versions: HistoryEntry[] };
      setHistory(data.versions);
    } catch {}
  }, [token]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const isDirty = config && editing && JSON.stringify(config) !== JSON.stringify(editing);

  const handleSave = async () => {
    if (!editing || !token) return;
    setSaving(true);
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/pricing-config`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: editing, reason: reason || null }),
      });
      const data = await res.json() as { success?: boolean; error?: string };
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      setToast({ type: 'success', msg: 'Đã lưu! Cache sẽ refresh sau 5 phút.' });
      setReason('');
      await fetchConfig();
      if (showHistory) await fetchHistory();
    } catch (err: any) {
      setToast({ type: 'error', msg: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (editing && config && !window.confirm('Hủy các thay đổi chưa lưu?')) return;
    setEditing(JSON.parse(JSON.stringify(config || DEFAULT_CONFIG)));
    setReason('');
  };

  const handleLoadDefault = () => {
    if (!window.confirm('Load DEFAULT_CONFIG (reset toàn bộ form về mặc định)?')) return;
    setEditing(JSON.parse(JSON.stringify(DEFAULT_CONFIG)));
  };

  // Live preview: per-agent với `previewInput` là input trực tiếp vào agent đó (y như cũ)
  // Per-agent markup override: null = inherit global, 0 = FREE, >0 = chargeable.
  const preview = useMemo(() => {
    if (!editing) return null;
    const rows = (Object.entries(editing.agents) as Array<[string, AgentPricing]>).map(([type, ap]) => {
      const mp = editing.model_pricing[ap.model];
      const agentMarkup: number = (typeof ap.markup === 'number') ? ap.markup : editing.markup;
      const isFree = agentMarkup === 0;
      if (!mp) return {
        type, model: ap.model, isFree, error: 'Model không tồn tại',
        input_usd: 0, output_usd: 0, cost_usd: 0, cost_vnd: 0, sell_vnd: 0, credits: 0, expected_output: 0, markup: agentMarkup, inherits: ap.markup == null,
        profit_vnd: 0,
      };
      const expectedOutput = Math.ceil(previewInput * ap.output_ratio);
      const input_usd = (previewInput * mp.input) / 1_000_000;
      const output_usd = (expectedOutput * mp.output) / 1_000_000;
      const cost_usd = input_usd + output_usd;
      const cost_vnd = cost_usd * editing.usd_to_vnd;
      const sell_vnd = isFree ? 0 : cost_vnd * agentMarkup;
      const credits = isFree ? 0 : Math.ceil(sell_vnd / editing.vnd_per_credit);
      const profit_vnd = sell_vnd - cost_vnd;  // founder lãi (≤ 0 khi FREE)
      return {
        type, model: ap.model, isFree,
        expected_output: expectedOutput,
        input_usd, output_usd, cost_usd, cost_vnd,
        sell_vnd, credits, markup: agentMarkup,
        inherits: ap.markup == null,
        profit_vnd,
      };
    });
    return rows;
  }, [editing, previewInput]);

  // Pipeline TRAIN: source = previewInput tokens → compute effective prompt input per agent
  //   translate:       input = source (user nhập 1:1)
  //   semantic-match:  input ≈ source + benchmark ≈ 2× source
  //   audit:           input ≈ source + AI_output + benchmark ≈ 2.3× source
  // Note: ratio này chỉ dùng riêng cho pipeline training preview, KHÔNG ảnh hưởng per-agent preview ở trên.
  const trainPipeline = useMemo(() => {
    if (!editing) return null;
    const sourceTokens = previewInput;
    // Audit factor derives from translate output_ratio: I_audit = I_translate × (1 + translate.output_ratio)
    // Source of truth — backend `preflightHelpers.ts` uses same formula at charge time.
    const translateRatio = editing.agents.translate?.output_ratio ?? 1.30;
    const PIPELINE_FACTORS: Record<string, number> = {
      translate: 1.0,
      'semantic-match': 2.0,
      audit: 1 + translateRatio,
    };
    const rows = (['translate', 'semantic-match', 'audit'] as const).map(type => {
      const ap = editing.agents[type];
      if (!ap) return null;
      const mp = editing.model_pricing[ap.model];
      if (!mp) return null;
      const effectiveInput = Math.ceil(sourceTokens * PIPELINE_FACTORS[type]);
      const expectedOutput = Math.ceil(effectiveInput * ap.output_ratio);
      const cost_usd = (effectiveInput * mp.input + expectedOutput * mp.output) / 1_000_000;
      const cost_vnd = cost_usd * editing.usd_to_vnd;
      const agentMarkup: number = (typeof ap.markup === 'number') ? ap.markup : editing.markup;
      const isFree = agentMarkup === 0;
      const sell_vnd = isFree ? 0 : cost_vnd * agentMarkup;
      const credits = isFree ? 0 : Math.ceil(sell_vnd / editing.vnd_per_credit);
      return { type, model: ap.model, effectiveInput, expectedOutput, cost_usd, cost_vnd, sell_vnd, credits, isFree };
    }).filter((x): x is NonNullable<typeof x> => x !== null);
    const total_cost_vnd = rows.reduce((s, r) => s + r.cost_vnd, 0);
    const total_cost_usd = rows.reduce((s, r) => s + r.cost_usd, 0);
    const total_sell_vnd = rows.reduce((s, r) => s + r.sell_vnd, 0);
    const total_credits = rows.reduce((s, r) => s + r.credits, 0);
    const profit_vnd = total_sell_vnd - total_cost_vnd;
    const margin = total_cost_vnd > 0 ? total_sell_vnd / total_cost_vnd : 0;
    return { rows, total_cost_usd, total_cost_vnd, total_sell_vnd, total_credits, profit_vnd, margin };
  }, [editing, previewInput]);

  if (loading) return <div className="p-6 text-slate-500">Loading pricing config...</div>;
  if (!editing) return <div className="p-6 text-red-600">Không load được config.</div>;

  const modelNames = Object.keys(editing.model_pricing);

  return (
    <div className="h-full overflow-auto bg-slate-50">
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 max-w-md ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
            {toast.type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            <span className="text-sm">{toast.msg}</span>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800">💰 Pricing Configuration (cost-based)</h2>
              <p className="text-xs text-slate-500">
                Version {currentVersion ?? '?'} — công thức: <code className="bg-slate-100 px-1 rounded">(input × in_price + output × out_price) × usd_to_vnd × markup / vnd_per_credit</code>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleLoadDefault} className="text-xs px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded">Load Default</button>
              <button
                onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHistory(); }}
                className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center gap-1.5"
              >
                <History className="w-3.5 h-3.5" />
                {showHistory ? 'Ẩn' : 'Xem'} lịch sử
              </button>
            </div>
          </div>

          {/* Global knobs */}
          <div className="mb-5 p-3 bg-indigo-50 rounded-lg">
            <div className="text-xs font-bold uppercase tracking-widest text-indigo-700 mb-2">Global Knobs</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[11px] text-slate-600 block mb-1">Markup factor</label>
                <input type="number" step="0.1" min="1" max="50" value={editing.markup}
                  onChange={(e) => setEditing({ ...editing, markup: parseFloat(e.target.value) || 1 })}
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono"/>
                <div className="text-[10px] text-slate-500 mt-0.5">cost × markup = sell VND (target 7.5×)</div>
              </div>
              <div>
                <label className="text-[11px] text-slate-600 block mb-1">VND per credit</label>
                <input type="number" step="0.001" min="0.001" max="100" value={editing.vnd_per_credit}
                  onChange={(e) => setEditing({ ...editing, vnd_per_credit: parseFloat(e.target.value) || 0 })}
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono"/>
                <div className="text-[10px] text-slate-500 mt-0.5">Gói Vừa 199K/1.5M = 0.133</div>
              </div>
              <div>
                <label className="text-[11px] text-slate-600 block mb-1">USD → VND</label>
                <input type="number" step="100" min="1000" max="100000" value={editing.usd_to_vnd}
                  onChange={(e) => setEditing({ ...editing, usd_to_vnd: parseFloat(e.target.value) || 0 })}
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono"/>
                <div className="text-[10px] text-slate-500 mt-0.5">Tỷ giá cố định</div>
              </div>
            </div>
          </div>

          {/* Agents table */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Agents</div>
              <button
                onClick={() => {
                  const name = window.prompt('Tên agent mới:');
                  if (!name || !name.trim()) return;
                  if (editing.agents[name]) { alert('Agent đã tồn tại'); return; }
                  setEditing({ ...editing, agents: { ...editing.agents, [name]: { model: modelNames[0] || '', output_ratio: 1.0, markup: null } } });
                }}
                className="text-xs px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Thêm agent
              </button>
            </div>
            <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold">Type</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Model</th>
                  <th className="text-left px-2 py-1.5 font-semibold" title="output = input × ratio">Output ratio</th>
                  <th className="text-left px-2 py-1.5 font-semibold" title="0 = FREE · trống = kế thừa global markup">Markup</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {(Object.entries(editing.agents) as Array<[string, AgentPricing]>).map(([type, ap]) => {
                  const isBuiltin = ['translate', 'qc', 'audit', 'semantic-match'].includes(type);
                  const inheritsGlobal = ap.markup == null;
                  const effectiveMarkup = inheritsGlobal ? editing.markup : ap.markup!;
                  return (
                    <tr key={type} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-mono text-xs">{type}</td>
                      <td className="px-2 py-1.5">
                        <select value={ap.model}
                          onChange={(e) => setEditing({ ...editing, agents: { ...editing.agents, [type]: { ...ap, model: e.target.value } } })}
                          className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded font-mono"
                        >
                          {modelNames.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" step="0.01" min="0" max="10" value={ap.output_ratio}
                          onChange={(e) => setEditing({ ...editing, agents: { ...editing.agents, [type]: { ...ap, output_ratio: parseFloat(e.target.value) || 0 } } })}
                          className="w-20 px-1.5 py-1 text-xs border border-slate-300 rounded font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number" step="0.1" min="0" max="50"
                            value={ap.markup ?? ''}
                            placeholder={`↻ ${editing.markup}`}
                            onChange={(e) => {
                              const v = e.target.value;
                              const markup = v === '' ? null : parseFloat(v);
                              setEditing({ ...editing, agents: { ...editing.agents, [type]: { ...ap, markup } } });
                            }}
                            className={`w-20 px-1.5 py-1 text-xs border rounded font-mono ${effectiveMarkup === 0 ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300'}`}
                            title="Để trống = kế thừa global. 0 = FREE. Số > 0 = override."
                          />
                          {effectiveMarkup === 0 && <span className="text-[9px] text-emerald-600 font-bold">FREE</span>}
                          {inheritsGlobal && <span className="text-[9px] text-slate-400" title="Kế thừa global markup">inherit</span>}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {!isBuiltin && (
                          <button
                            onClick={() => {
                              if (!window.confirm(`Xóa agent "${type}"?`)) return;
                              const { [type]: _, ...rest } = editing.agents;
                              setEditing({ ...editing, agents: rest });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Model pricing table */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Model pricing (USD per 1M tokens)</div>
              <button
                onClick={() => {
                  const name = window.prompt('Tên model mới (VD: gemini-3.5-flash):');
                  if (!name || !name.trim()) return;
                  if (editing.model_pricing[name]) { alert('Model đã tồn tại'); return; }
                  setEditing({ ...editing, model_pricing: { ...editing.model_pricing, [name]: { input: 0, output: 0 } } });
                }}
                className="text-xs px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Thêm model
              </button>
            </div>
            <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold">Model</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Input $/1M</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Output $/1M</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Ratio out/in</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {(Object.entries(editing.model_pricing) as Array<[string, ModelPricing]>).map(([model, mp]) => {
                  const usedBy = (Object.entries(editing.agents) as Array<[string, AgentPricing]>).filter(([_, ap]) => ap.model === model).map(([t]) => t);
                  return (
                    <tr key={model} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-mono text-xs">
                        {model}
                        {usedBy.length > 0 && <div className="text-[10px] text-slate-400">used: {usedBy.join(', ')}</div>}
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" step="0.01" min="0" max="100" value={mp.input}
                          onChange={(e) => setEditing({ ...editing, model_pricing: { ...editing.model_pricing, [model]: { ...mp, input: parseFloat(e.target.value) || 0 } } })}
                          className="w-20 px-1.5 py-1 text-xs border border-slate-300 rounded font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" step="0.01" min="0" max="1000" value={mp.output}
                          onChange={(e) => setEditing({ ...editing, model_pricing: { ...editing.model_pricing, [model]: { ...mp, output: parseFloat(e.target.value) || 0 } } })}
                          className="w-20 px-1.5 py-1 text-xs border border-slate-300 rounded font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-xs text-slate-500 font-mono">
                        {mp.input > 0 ? (mp.output / mp.input).toFixed(1) + '×' : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {usedBy.length === 0 && (
                          <button
                            onClick={() => {
                              if (!window.confirm(`Xóa model "${model}"?`)) return;
                              const { [model]: _, ...rest } = editing.model_pricing;
                              setEditing({ ...editing, model_pricing: rest });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Live preview */}
          <div className="mb-5 p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-600 flex items-center gap-1">
                <Info className="w-3 h-3" /> Live Preview
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500">Input tokens:</label>
                <input type="number" step="1000" min="100" max="1000000" value={previewInput}
                  onChange={(e) => setPreviewInput(parseInt(e.target.value) || 10000)}
                  className="w-28 px-2 py-1 text-xs border border-slate-300 rounded font-mono"
                />
              </div>
            </div>
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-1 font-semibold">Agent</th>
                  <th className="text-right py-1 font-semibold" title="Expected output tokens = input × output_ratio">Exp Out</th>
                  <th className="text-right py-1 font-semibold" title="Input cost: prompt_input_tokens × model.input_price / 1M">In USD</th>
                  <th className="text-right py-1 font-semibold" title="Output cost: expected_output × model.output_price / 1M">Out USD</th>
                  <th className="text-right py-1 font-semibold">Total Cost</th>
                  <th className="text-right py-1 font-semibold">Sell VND</th>
                  <th className="text-right py-1 font-semibold">Credits</th>
                  <th className="text-right py-1 font-semibold">Margin</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {preview?.map(r => {
                  const inPct = r.cost_usd > 0 ? (r.input_usd / r.cost_usd) * 100 : 0;
                  const outPct = r.cost_usd > 0 ? (r.output_usd / r.cost_usd) * 100 : 0;
                  return (
                    <tr key={r.type} className="border-t border-slate-200">
                      <td className="py-1">
                        {r.type} {r.isFree && <span className="text-[10px] text-emerald-600 ml-1">FREE</span>}
                        <div className="text-[10px] text-slate-400 font-normal">{r.model}</div>
                      </td>
                      <td className="text-right py-1">{r.expected_output.toLocaleString()}</td>
                      <td className="text-right py-1 text-slate-500">
                        ${r.input_usd.toFixed(6)}
                        <div className="text-[9px] opacity-60">{inPct.toFixed(0)}%</div>
                      </td>
                      <td className="text-right py-1 text-slate-500">
                        ${r.output_usd.toFixed(6)}
                        <div className="text-[9px] opacity-60">{outPct.toFixed(0)}%</div>
                      </td>
                      <td className="text-right py-1 font-semibold">
                        ${r.cost_usd.toFixed(5)}
                        <div className="text-[10px] text-slate-400 font-normal">{r.cost_vnd.toFixed(1)}đ</div>
                      </td>
                      <td className="text-right py-1">{r.isFree ? '—' : r.sell_vnd.toFixed(0) + 'đ'}</td>
                      <td className="text-right py-1 text-indigo-700 font-bold">
                        {r.credits.toLocaleString()}
                        {!r.isFree && r.inherits && <span className="text-[9px] text-slate-400 ml-1" title="Dùng global markup">↻</span>}
                        {!r.isFree && !r.inherits && <span className="text-[9px] text-amber-600 ml-1" title={`Agent override markup=${r.markup}`}>★{r.markup}×</span>}
                      </td>
                      <td className="text-right py-1 text-emerald-700">{!r.isFree && r.cost_vnd > 0 ? (r.sell_vnd / r.cost_vnd).toFixed(1) + '×' : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-[10px] text-slate-400 mt-2">
              <span className="font-semibold text-slate-500 uppercase tracking-wide">Công thức:</span>{' '}
              <code className="text-[10px]">cost = (in × in_price + out × out_price) / 1M</code>{' · '}
              <code className="text-[10px]">credits = ⌈cost × usd_vnd × markup / vnd_credit⌉</code>
              <span className="italic ml-2">· Giả định output_ratio cố định per agent (config ở trên) — thực tế Gemini output có thể dao động ±20-40%.</span>
            </div>
          </div>

          {/* 🎯 TRAINING PIPELINE P&L — chốt chính sách giá */}
          {trainPipeline && (
            <div className="mb-5 p-4 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-lg border border-indigo-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-indigo-700">🎯 Pipeline huấn luyện — P&L per file</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Source = <span className="font-mono font-semibold">{previewInput.toLocaleString()}</span> tokens ·
                    translate + match + audit (giả định audit input ≈ 2.3× source, match ≈ 2× source vì prompt bao gồm benchmark + AI output)
                  </div>
                </div>
              </div>

              <table className="w-full text-xs mb-3">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left py-1 font-semibold">Bước</th>
                    <th className="text-right py-1 font-semibold">Prompt input</th>
                    <th className="text-right py-1 font-semibold">Exp Out</th>
                    <th className="text-right py-1 font-semibold">Chi phí Gemini</th>
                    <th className="text-right py-1 font-semibold">Bán cho user</th>
                    <th className="text-right py-1 font-semibold">Credits</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {trainPipeline.rows.map(r => (
                    <tr key={r.type} className="border-t border-indigo-100">
                      <td className="py-1">
                        <span className="font-semibold text-slate-700">{r.type}</span>
                        {r.isFree && <span className="text-[9px] text-emerald-600 ml-1 font-bold">FREE</span>}
                        <div className="text-[10px] text-slate-400 font-normal">{r.model}</div>
                      </td>
                      <td className="text-right py-1">{r.effectiveInput.toLocaleString()}</td>
                      <td className="text-right py-1">{r.expectedOutput.toLocaleString()}</td>
                      <td className="text-right py-1 text-rose-700">
                        ${r.cost_usd.toFixed(5)}
                        <div className="text-[10px] opacity-70">{r.cost_vnd.toFixed(0)}đ</div>
                      </td>
                      <td className="text-right py-1 text-emerald-700">
                        {r.isFree ? '—' : r.sell_vnd.toFixed(0) + 'đ'}
                      </td>
                      <td className="text-right py-1 text-indigo-700 font-bold">{r.credits.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-indigo-300 font-mono">
                    <td className="py-2 text-xs font-bold text-slate-800" colSpan={3}>TỔNG PIPELINE TRAIN</td>
                    <td className="text-right py-2 text-rose-800 font-bold">
                      ${trainPipeline.total_cost_usd.toFixed(5)}
                      <div className="text-[10px] opacity-70 font-normal">{trainPipeline.total_cost_vnd.toFixed(0)}đ</div>
                    </td>
                    <td className="text-right py-2 text-emerald-800 font-bold">{trainPipeline.total_sell_vnd.toFixed(0)}đ</td>
                    <td className="text-right py-2 text-indigo-800 font-bold">{trainPipeline.total_credits.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>

              {/* P&L summary stripes */}
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="p-2 bg-white rounded border border-rose-200">
                  <div className="text-[10px] text-rose-600 font-semibold uppercase tracking-wide">Chi phí vào</div>
                  <div className="font-mono font-bold text-rose-700 text-lg leading-tight">{trainPipeline.total_cost_vnd.toFixed(0)}đ</div>
                  <div className="text-[10px] text-rose-500">${trainPipeline.total_cost_usd.toFixed(5)}</div>
                </div>
                <div className="p-2 bg-white rounded border border-emerald-200">
                  <div className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide">Bán ra</div>
                  <div className="font-mono font-bold text-emerald-700 text-lg leading-tight">{trainPipeline.total_sell_vnd.toFixed(0)}đ</div>
                  <div className="text-[10px] text-emerald-500">{trainPipeline.total_credits.toLocaleString()} credits</div>
                </div>
                <div className="p-2 bg-white rounded border border-indigo-200">
                  <div className="text-[10px] text-indigo-600 font-semibold uppercase tracking-wide">Lãi/file</div>
                  <div className="font-mono font-bold text-indigo-700 text-lg leading-tight">{trainPipeline.profit_vnd.toFixed(0)}đ</div>
                  <div className="text-[10px] text-indigo-500">≈ ${(trainPipeline.profit_vnd / editing.usd_to_vnd).toFixed(5)}</div>
                </div>
                <div className="p-2 bg-white rounded border border-amber-200">
                  <div className="text-[10px] text-amber-600 font-semibold uppercase tracking-wide">Margin</div>
                  <div className="font-mono font-bold text-amber-700 text-lg leading-tight">{trainPipeline.margin.toFixed(1)}×</div>
                  <div className="text-[10px] text-amber-500">{trainPipeline.total_cost_vnd > 0 ? (((trainPipeline.total_sell_vnd - trainPipeline.total_cost_vnd) / trainPipeline.total_sell_vnd) * 100).toFixed(0) + '%' : '—'} gross</div>
                </div>
              </div>
            </div>
          )}

          {/* Reason */}
          <div className="mb-4">
            <label className="text-[11px] text-slate-500 block mb-1">Lý do đổi (audit trail):</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder='VD: "Tăng markup 7.5→8.5 để bù audit model đắt hơn"'
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={!isDirty || saving}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${isDirty && !saving ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
            </button>
            <button onClick={handleReset} disabled={!isDirty}
              className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${isDirty ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-slate-50 text-slate-400 cursor-not-allowed'}`}
            >
              <RotateCcw className="w-4 h-4" />
              Hủy
            </button>
            {isDirty && <span className="text-xs text-amber-600 italic">Có thay đổi chưa lưu</span>}
          </div>

          <div className="mt-3 text-[11px] text-slate-400 italic">
            ⚠ Sau khi lưu, Worker cache 5 phút mới refresh — jobs chạy trong khoảng này vẫn dùng rate cũ.
          </div>
        </div>

        {showHistory && (
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3">📜 Pricing History (last 50)</h3>
            {history.length === 0 ? (
              <div className="text-xs text-slate-400">Chưa có lịch sử.</div>
            ) : (
              <div className="space-y-2">
                {history.map((h) => (
                  <div key={h.version} className="border-l-2 border-slate-200 pl-3 py-1 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-700">v{h.version}</span>
                      <span className="text-slate-500">{new Date(h.effective_from).toLocaleString('vi-VN')}</span>
                    </div>
                    <div className="text-slate-600 mt-0.5 font-mono text-[10px]">
                      {h.config.agents ? (
                        <>markup={h.config.markup} · VND/credit={h.config.vnd_per_credit} · agents={Object.keys(h.config.agents).length}</>
                      ) : (
                        <span className="text-amber-600">legacy schema (multiplier)</span>
                      )}
                    </div>
                    {h.reason && <div className="text-slate-500 italic mt-0.5">"{h.reason}"</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingConfigPanel;
